import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import {
    ChevronRight, ChevronLeft, Laptop, Zap, Instagram, Youtube, Facebook
} from "lucide-react";
import "./index.css";
import POSPage from "./POSPage";

function LogoIcon({ size = 32, className = "" }) {
    return (
        <svg 
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 100 100" 
            width={size} 
            height={size} 
            className={className}
            style={{ display: "inline-block", verticalAlign: "middle" }}
        >
            <defs>
                <linearGradient id="bgGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#090d16" />
                    <stop offset="100%" stopColor="#1e1b4b" />
                </linearGradient>
                <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="3" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>
            </defs>
            <rect x="10" y="10" width="80" height="80" rx="24" fill="#6366f1" opacity="0.3" filter="url(#glow)" />
            <rect x="10" y="10" width="80" height="80" rx="24" fill="url(#bgGrad)" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
            <circle cx="50" cy="50" r="34" fill="none" stroke="#6366f1" strokeWidth="1.5" strokeDasharray="4 4" opacity="0.6" />
            <circle cx="50" cy="50" r="26" fill="none" stroke="#8b5cf6" strokeWidth="1.5" strokeDasharray="1 3" opacity="0.4" />
            <path 
                d="M 50 34 A 8 8 0 0 1 58 42 A 6 6 0 0 1 64 48 A 7 7 0 0 1 57 55 L 43 55 A 7 7 0 0 1 36 48 A 7 7 0 0 1 42 41 A 8 8 0 0 1 50 34 Z" 
                fill="#ffffff" 
                filter="drop-shadow(0px 2px 4px rgba(99, 102, 241, 0.4))" 
            />
            <g transform="translate(50, 46.5) scale(0.6)">
                <path d="M -5,-2 A 6,6 0 0,1 5,-2" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" />
                <polygon points="5,-2 2,-5 8,-5" fill="#6366f1" transform="rotate(-15, 5, -2)" />
                <path d="M 5,2 A 6,6 0 0,1 -5,2" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" />
                <polygon points="-5,2 -2,5 -8,5" fill="#6366f1" transform="rotate(165, -5, 2)" />
            </g>
        </svg>
    );
}

function GridBackground() {
    return (
        <>
            <div className="ambient-grid" />
            <div className="scanlines" />
            <div className="ambient-particles" />
        </>
    );
}

function Preloader({ onComplete }) {
    const [status, setStatus] = useState("Connecting to server...");
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        const sequence = [
            { t: 0, s: "Connecting to server...", p: 15 },
            { t: 500, s: "Loading modules...", p: 40 },
            { t: 1100, s: "Authenticating session...", p: 75 },
            { t: 1700, s: "Fetching dashboard data...", p: 95 },
            { t: 2300, s: "Ready.", p: 100 },
        ];

        sequence.forEach(({ t, s, p }) => {
            setTimeout(() => {
                setStatus(s);
                setProgress(p);
            }, t);
        });

        const timer = setTimeout(onComplete, 2700);
        return () => clearTimeout(timer);
    }, [onComplete]);

    return (
        <motion.div
            className="preloader"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
            <div className="preloader-glow" />
            <div className="preloader-content-v2">
                <div className="preloader-logo-v2">
                    <LogoIcon size={38} />
                    <span className="logo-x">XYNK</span>
                    <span className="logo-c">CLOUD</span>
                </div>
                
                <div className="preloader-spinner-wrap">
                    <div className="preloader-spinner" />
                </div>

                <div className="preloader-bar-wrap-v2">
                    <div className="preloader-bar-inner-v2" style={{ width: `${progress}%` }} />
                </div>
                
                <div className="preloader-status-v2">
                    <span>{status}</span>
                    <span>{progress}%</span>
                </div>
            </div>
        </motion.div>
    );
}

function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = menuOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [menuOpen]);

    const close = () => setMenuOpen(false);
    const navLinks = [
        { name: "Home", href: "/#" },
        { name: "Features", href: "#features" },
        { name: "Pricing", href: "#pricing" },
        { name: "FAQ", href: "#faq" }
    ];

    return (
        <>
            <motion.nav
                className={`navbar ${scrolled ? "scrolled" : ""}`}
                initial={{ y: -80, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
            >
                <div className="container nav-inner">
                    <Link to="/" className="nav-logo">
                        <LogoIcon size={28} />
                        <span>XYNK<span className="accent">CLOUD</span></span>
                    </Link>

                    <div className="nav-links">
                        {navLinks.map((link) => (
                            <a
                                key={link.name}
                                href={link.href}
                            >
                                {link.name}
                            </a>
                        ))}
                        <div
                            className="dropdown"
                            onMouseEnter={() => setDropdownOpen(true)}
                            onMouseLeave={() => setDropdownOpen(false)}
                        >
                            <span className="dropdown-trigger">Products ▾</span>
                            <div className={`dropdown-menu ${dropdownOpen ? "open" : ""}`}>
                                <Link to="/cafeos">
                                    <Laptop size={14} /> CafeOS
                                </Link>
                            </div>
                        </div>
                    </div>

                    <a href="#contact" className="nav-cta-btn">
                        Request Trial <ChevronRight size={14} />
                    </a>

                    <button
                        className={`hamburger ${menuOpen ? "active" : ""}`}
                        onClick={() => setMenuOpen((o) => !o)}
                        aria-label="Toggle menu"
                    >
                        <span /><span /><span />
                    </button>
                </div>
            </motion.nav>

            <AnimatePresence>
                {menuOpen && (
                    <motion.div
                        className="mobile-overlay open"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                    >
                        <button className="mobile-close" onClick={close} aria-label="Close menu">✕</button>
                        {navLinks.map((link, i) => (
                            <motion.a
                                key={link.name}
                                href={link.href}
                                onClick={close}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: i * 0.05 }}
                            >
                                {link.name}
                            </motion.a>
                        ))}
                        <Link to="/cafeos" onClick={close}>CafeOS</Link>
                        <a href="#contact" className="nav-cta-btn" onClick={close}>
                            Request Trial
                        </a>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}

function Footer() {
    const footerSocials = [
        { icon: <Instagram size={18} />, link: "https://www.instagram.com/xynkcloud/" },
        { icon: <Youtube size={18} />, link: "#" },
        { icon: <Facebook size={18} />, link: "#" },
    ];

    return (
        <footer className="footer footer-pos">
            <div className="container">
                <div className="footer-grid">
                    <motion.div className="footer-brand" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                        <div className="footer-logo">
                            <LogoIcon size={26} />
                            <span>XYNK<span className="accent">CLOUD</span></span>
                        </div>
                        <p>Purpose-built POS and operating systems for modern gaming cafes, cyber cafes, and esports arenas.</p>
                        <div className="footer-socials">
                            {footerSocials.map((s, i) => (
                                <motion.a href={s.link} key={i} className="footer-social-icon" whileHover={{ scale: 1.15, backgroundColor: "var(--c-cyan)", color: "#000" }}>
                                    {s.icon}
                                </motion.a>
                            ))}
                        </div>
                    </motion.div>

                    <motion.div className="footer-col" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
                        <h4>Quick Links</h4>
                        <a href="#features">Features</a>
                        <a href="#pricing">Pricing</a>
                        <a href="#faq">FAQ</a>
                        <a href="#contact">Contact Us</a>
                    </motion.div>

                    <motion.div className="footer-col" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
                        <h4>Our Solutions</h4>
                        <Link to="/">POS Software</Link>
                        <Link to="/cafeos">CafeOS</Link>
                    </motion.div>

                    <motion.div className="footer-col" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }}>
                        <h4>Contact</h4>
                        <a href="mailto:info@xynkcloud.com">info@xynkcloud.com</a>
                        <a href="tel:+919878483865">098784 83865</a>
                        <span className="footer-address">1st Floor, Ek onkar city, SCO No. 3, NH, Kharar, Punjab</span>
                    </motion.div>
                </div>

                <div className="footer-bottom">
                    <p>© {new Date().getFullYear()} Xynk Cloud. All Rights Reserved.</p>
                </div>
            </div>
        </footer>
    );
}

function ComingSoon({ title }) {
    return (
        <div className="coming-soon-container">
            <div className="coming-soon-bg" />
            <motion.div
                className="coming-soon-content"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
            >
                <div className="cs-warning-icon">
                    <Zap size={64} fill="#FFD700" color="#FFD700" />
                </div>
                <h1 className="cs-title">{title}</h1>
                <div className="cs-divider" />
                <h2 className="cs-subtitle">COMING SOON</h2>
                <p className="cs-text">
                    We are currently building the ultimate {title.toLowerCase()} experience.
                    Stay tuned for something revolutionary.
                </p>
                <Link to="/" className="cs-back-btn">
                    <ChevronLeft size={20} /> Back to Home
                </Link>
            </motion.div>
            <div className="cs-grid-overlay" />
        </div>
    );
}

export default function App() {
    const [loading, setLoading] = useState(true);
    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

    return (
        <Router>
            <ScrollToTop />
            <AnimatePresence mode="wait">
                {loading ? (
                    <Preloader key="preloader" onComplete={() => setLoading(false)} />
                ) : (
                    <div key="content">
                        <motion.div className="scroll-progress" style={{ scaleX }} />
                        <div style={{ position: "relative", overflowX: "hidden" }}>
                            <GridBackground />
                            <div className="ambient-orbs-container">
                                <div className="ambient-orb orb-indigo" />
                                <div className="ambient-orb orb-cyan" />
                            </div>
                            <Navbar />
                            <Routes>
                                <Route path="/" element={<POSPage />} />
                                <Route path="/cafeos" element={<ComingSoon title="CafeOS" />} />
                            </Routes>
                            <Footer />
                        </div>
                    </div>
                )}
            </AnimatePresence>
        </Router>
    );
}

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);
    return null;
}